/**
 * A Recording that already exists, as the create form's draft (WMC-557).
 *
 * The existence lookup (contract 4.1.4.9, §4.6 lookup 6) answers with the
 * Recording in the **create payload's own shapes** — `recording`, `artists`,
 * `release` — exactly what a create of it would have sent, and nothing of
 * any artist's own entry (no contributions, no persona). "Add Recording"
 * shows that Recording back to the operator, locked, so it has to be mapped
 * onto the same `Row` fields the form hydrates from in edit mode.
 *
 * Pure (no React), so it sits in jest's DOM-less project and the mapping is
 * testable without mounting the form. Because the lookup speaks the create
 * payload's language, this is the inverse of `buildCreateRecordingEnvelope`:
 * field for field, what that builder reads off the form this writes back.
 */
import type { Row } from '../view/PageLayout/MasterDiscographyTypes';
import type { LookupRecordingResponseExisting } from 'shared_components/generated/admin';

/**
 * A main artist of the existing Recording, in the form's `ArtistEntry` shape.
 *
 * Declared structurally rather than imported from `ArtistEditorModal.tsx`
 * (which is JSX), the same way `ArtistEntryLike` is in ./recordingOpBuilders.
 * `ArtistEntry` is assignable from this. The featured status is the one the
 * Recording's lineup carries, in the form's vocabulary (`nfa` is the form's
 * `nf`); these artists are shown, never submitted — the add arm sends no
 * `artists` at all (`add-no-artists`).
 */
export interface ExistingArtistEntry {
  name: string;
  id?: string;
  isFeatured: boolean;
  featuredStatus: 'fa' | 'ofa' | 'nf';
  type?: 'persona' | 'group';
}

/** The form's recording and release state, hydrated from the lookup. */
export interface ExistingRecordingDraft {
  formData: Partial<Row>;
  labels: string[];
  catNumbers: string[];
  versions: string[];
  isrcs: string[];
  pNames: string[];
  artists: ExistingArtistEntry[];
}

const yearOf = (dateReleased: string | null | undefined): string =>
  (dateReleased ?? '').slice(0, 4);

const featuredStatusOf = (
  status: LookupRecordingResponseExisting['artists'][number]['featuredStatus']
): ExistingArtistEntry['featuredStatus'] => {
  if (status === 'ofa') return 'ofa';
  if (status === 'nfa') return 'nf';
  return 'fa';
};

/**
 * The existing Recording as the create form's draft, and as its baseline.
 *
 * The caller seeds `originalValues` with this same draft, so the form's
 * "changed" indicators stay quiet — nothing about the Recording has been
 * changed, it has been *shown*. Contributions are not part of the draft on
 * purpose: they are the one thing the operator supplies (`add-contributions-
 * own`), and the lookup does not return any artist's.
 */
export const existingRecordingDraft = (
  existing: LookupRecordingResponseExisting
): ExistingRecordingDraft => {
  const { recording, release } = existing;
  const artists: ExistingArtistEntry[] = (existing.artists ?? [])
    .map((artist): ExistingArtistEntry | null => {
      const name = (artist.stageName ?? '').trim();
      if (!name) return null;
      const featuredStatus = featuredStatusOf(artist.featuredStatus);
      return {
        name,
        id: artist.artistId,
        isFeatured: featuredStatus !== 'nf',
        featuredStatus,
        type: (artist.members ?? []).length > 0 ? 'group' : 'persona',
      };
    })
    .filter((a): a is ExistingArtistEntry => a !== null);

  return {
    formData: {
      title: recording.recordingTitle,
      duration: recording.duration ?? '',
      live: recording.live ?? '',
      yearRecorded: recording.yearRecorded ?? '',
      location: recording.countryOfRecording ?? '',
      release: release?.releaseTitle ?? '',
      year: yearOf(release?.dateReleased),
      format: release?.format ?? '',
      mediaType: release?.mediaType ?? '',
      barcode: release?.barcode ?? '',
    },
    labels: release?.releaseLabels ?? [],
    catNumbers: release?.catNumbers ?? [],
    versions: recording.recordingVersion ?? [],
    isrcs: recording.isrcs ?? [],
    pNames: recording.pNames ?? [],
    artists,
  };
};
